import React, { useEffect, useState } from "react";
import { CircularProgress, Typography } from "@material-ui/core";
import {
  fetchGlobalDepartmentInfo,
  fetchGlobalGroupInfo,
  fetchProductWrapperInfo,
} from "../../../../../services/menuApp/printerMenuService";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../../utils/consts";
import ProductsModalContent from "./ProductsModalContent";
import { useRouteMatch } from "react-router-dom";
import { fetchAllStockItemsProductInfo } from "../../../../../services/inventory/stockItems";
import { AxiosError } from "axios";

interface IndexProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedWrapperIdList: any;
  handleSubmitRewardsTypePoints: (
    ids: string[],
    isEdit: boolean,
    rewardId: string | null,
  ) => void;
  isEdit: boolean;
  rewardId: any;
  isLoadingButton: boolean;
  handleSelectProduct: any;
  setSelectedWrapperIdList: any;
  selectedWrapperIdListInitial: any;

  offerItemMap: any;
  selectedWrapperIdListInitialNew: any;
  setOfferItemMap: any;
  offerItemMapInitial: any;
}

// Helper for restructuring
const convertInputToOutput = (productWrapper: any) => {
  const directories: any = {};
  productWrapper.forEach((item: any) => {
    const directoryId = item.directoryId;
    directories[directoryId] = directories[directoryId] || [];
    directories[directoryId].push(item);
  });

  const buildWrapper = (directoryId: any) => {
    const items = directories[directoryId] || [];
    return items.map((item: any) => {
      const wrapper = buildWrapper(item.id);
      return wrapper.length > 0 ? { ...item, wrapper } : item;
    });
  };

  return buildWrapper(-99);
};

const handleMenuRestructure = (
  groupNode: any,
  departmentNode: any,
  productNode: any,
) => {
  if (!groupNode || !departmentNode || !productNode) return [];

  const sortedGroups = [...groupNode].sort((a, b) => a.viewOrder - b.viewOrder);
  const sortedDepartments = [...departmentNode].sort(
    (a, b) => a.viewOrder - b.viewOrder,
  );
  const sortedProducts = [...productNode].sort(
    (a, b) => a.viewOrder - b.viewOrder,
  );
  const output: any = [];

  const departmentMap = sortedDepartments.reduce((acc, dep) => {
    acc[dep.groupId] = acc[dep.groupId] || [];
    if (
      dep.id > 0 &&
      !["Online Hidden", "Templates", "Hidden Food", "Hidden Drinks"].includes(
        dep.name,
      )
    ) {
      acc[dep.groupId].push({ ...dep, product: [] });
    }
    return acc;
  }, {});

  const productMap = sortedProducts.reduce((acc, prod) => {
    acc[prod.categoryId] = acc[prod.categoryId] || [];
    if (prod.id > 0) {
      acc[prod.categoryId].push(prod);
    }
    return acc;
  }, {});

  sortedGroups.forEach((groupItem) => {
    if (groupItem.id <= 0) return;
    const groupDepartments = departmentMap[groupItem.id] || [];
    groupDepartments.forEach((department: any) => {
      const products = productMap[department.id] || [];
      if (products.length > 0) {
        department.product = convertInputToOutput(products);
      }
    });

    const populatedDepartments = groupDepartments.filter(
      (department: any) => department.product.length > 0,
    );

    if (populatedDepartments.length > 0) {
      output.push({ ...groupItem, department: populatedDepartments });
    }
  });

  return output;
};

const ProductModal: React.FC<IndexProps> = (props) => {
  const match: any = useRouteMatch();
  const locationId = (match.params as any).locationId;
  const [menuNodes, setMenuNodes] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [menuProducts, setMenuProducts] = useState<any>({});

  const handleRestructureProducts = (input: any) => {
    const output = Object.values(input).reduce((acc: any, item: any) => {
      const key = item.itemWrapperId;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
    return output;
  };

  /**
   * Function to fetch stock items products information and update the 'menuProducts' state.
   */
  const handleGetStockItemsProductsInfo = async () => {
    try {
      // Attempt to fetch stock items products information using the 'fetchAllStockItemsProductInfo' API
      const res = await fetchAllStockItemsProductInfo(match.params.locationId);
      const output = handleRestructureProducts(res.data.data);
      // Set the 'menuProducts' state with the retrieved data from the response
      setMenuProducts(output);
    } catch (error) {
      const err: any = error as AxiosError;
      if (err.response.status !== 404) {
        // If an error occurs during the API call, set the 'error' state with an unexpected error message
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [groupRes, departmentRes, productRes, stockRes] =
          await Promise.all([
            fetchGlobalGroupInfo(locationId),
            fetchGlobalDepartmentInfo(locationId),
            fetchProductWrapperInfo(locationId),
            fetchAllStockItemsProductInfo(locationId),
          ]);

        const output = handleRestructureProducts(stockRes.data.data);
        setMenuProducts(output);

        const menuNodes: any = handleMenuRestructure(
          groupRes.data.data,
          departmentRes.data.data,
          productRes.data.data,
        );

        if (menuNodes.length > 0) {
          setMenuNodes(menuNodes);
          setDepartments(menuNodes[0].department);
        }
      } catch (err) {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [locationId]);

  if (error) {
    return (
      <div
        style={{
          marginTop: "16%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Typography variant="h3">{error}</Typography>
      </div>
    );
  }
  return (
    <ProductsModalContent
      {...props}
      menuNodes={menuNodes}
      departmentList={departments}
      menuProducts={menuProducts}
    />
  );
};

export default ProductModal;
